import {
  Boxes,
  Clock,
  LayoutDashboard,
  Loader2,
  Pencil,
  Share2,
  Trash2,
  Workflow,
} from "lucide-react";
import GlassContainer from "@/shared/components/glasscontainer";
import type { WorkspaceSummary } from "../models/Workspace";

export interface WorkspaceCardProps {
  workspace: WorkspaceSummary;
  deleting: boolean;
  onOpen: () => void;
  onDashboard: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/** "hace 5 min", "hace 3 días"… fallback a la fecha absoluta más allá del mes. */
function timeAgo(iso: string): string {
  const date = new Date(iso);
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat("es", { numeric: "auto" });
  if (seconds < 60) return rtf.format(-seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return rtf.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 24) return rtf.format(-hours, "hour");
  const days = Math.round(hours / 24);
  if (days < 31) return rtf.format(-days, "day");
  return date.toLocaleDateString();
}

/** Un emoji corto se pinta tal cual; cualquier otra cosa se trata como URL de imagen. */
function isEmoji(icon: string | null | undefined): icon is string {
  return !!icon && icon.length <= 4 && !icon.includes("/");
}

export function WorkspaceCard({
  workspace,
  deleting,
  onOpen,
  onDashboard,
  onEdit,
  onDelete,
}: WorkspaceCardProps) {
  const { isOwner, icon, color } = workspace;

  return (
    <GlassContainer className="group relative overflow-hidden rounded-3xl shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] transition-all duration-500 hover:-translate-y-1.5 hover:border-white hover:bg-glass-shine hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.12)]">
      <div className="relative flex min-w-0 flex-col p-5">
        <div className="flex min-w-0 items-start gap-3.5">
          <button
            onClick={onOpen}
            title="Abrir workspace"
            className="flex size-12 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br text-white shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3"
            style={
              color
                ? { backgroundImage: `linear-gradient(to bottom right, ${color}, ${color}cc)` }
                : undefined
            }
          >
            {isEmoji(icon) ? (
              <span className="text-2xl leading-none">{icon}</span>
            ) : icon ? (
              <img src={icon} alt="" className="size-full object-cover" />
            ) : (
              <Boxes className="size-6" />
            )}
          </button>

          <div className="min-w-0 flex-1">
            <button
              onClick={onOpen}
              className="block max-w-full cursor-pointer truncate text-left text-[15px] font-bold tracking-tight text-foreground/90 transition-colors group-hover:text-accent"
            >
              {workspace.name}
            </button>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-foreground/40">
              <Clock className="size-3 shrink-0" />
              <span className="truncate" title={new Date(workspace.updatedAt).toLocaleString()}>
                {timeAgo(workspace.updatedAt)}
              </span>
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <button
              onClick={onDashboard}
              className="flex size-8 cursor-pointer items-center justify-center rounded-xl border border-black/5 bg-white/50 text-foreground/50 opacity-0 transition-all hover:bg-white hover:text-accent hover:shadow-md focus-visible:opacity-100 group-hover:opacity-100"
              title={workspace.hasDashboard ? "Dashboard" : "Crear dashboard"}
            >
              <LayoutDashboard className="size-3.5" />
            </button>
            {isOwner && (
              <>
                <button
                  onClick={onEdit}
                  className="flex size-8 cursor-pointer items-center justify-center rounded-xl border border-black/5 bg-white/50 text-foreground/50 opacity-0 transition-all hover:bg-white hover:text-accent hover:shadow-md focus-visible:opacity-100 group-hover:opacity-100"
                  title="Editar workspace"
                >
                  <Pencil className="size-3.5" />
                </button>
                <button
                  onClick={onDelete}
                  disabled={deleting}
                  className="flex size-8 cursor-pointer items-center justify-center rounded-xl border border-black/5 bg-white/50 text-foreground/50 opacity-0 transition-all hover:border-rose-500 hover:bg-rose-500 hover:text-white hover:shadow-md focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-50 group-hover:opacity-100"
                  title="Eliminar workspace"
                >
                  {deleting ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="size-3.5" />
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {workspace.description && (
          <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-foreground/50">
            {workspace.description}
          </p>
        )}

        <div className="mt-4 flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-black/5 bg-gray/10 px-2.5 py-1 text-[10px] font-bold text-gray">
            <Workflow className="size-3" />
            {workspace.workflowCount}{" "}
            {workspace.workflowCount === 1 ? "AUTOMATIZACIÓN" : "AUTOMATIZACIONES"}
          </span>
          {workspace.hasDashboard && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-accent/20 bg-accent/10 px-2.5 py-1 text-[10px] font-bold text-accent">
              <LayoutDashboard className="size-3" />
              DASHBOARD
            </span>
          )}
          {!isOwner && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-[10px] font-bold text-amber-600"
              title="Llegaste por una automatización compartida: podés verlo, pero no modificarlo"
            >
              <Share2 className="size-3" />
              COMPARTIDO
            </span>
          )}
        </div>
      </div>
    </GlassContainer>
  );
}
